import React, { useState } from 'react';
import {
  LegacyAutocomplete,
  LegacyAutocompleteInput,
  LegacyAutocompleteOptions,
} from '@flixbus/honeycomb-react';
import { useVoucherFormContext } from '../../Context';
import { Relation, AllowedRelationsRule, ForbiddenRelationsRule } from '../../../../types/Rules';
import {
  CountriesOptions,
  AutocompleteCountry,
  CountriesAutocomplete,
} from '../AllowedandForbiddenCountries/Countries';
import { useCityRelations } from './useCityRelations';

interface RelationPairInputsProps {
  relationType: Relation;
  relationId: string;
}

const RelationPairInputs: React.FC<RelationPairInputsProps> = ({ relationType, relationId }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  const isCity = relationType === 'allowed_cities' || relationType === 'forbidden_cities';

  const ruleType =
    relationType === 'allowed_countries' || relationType === 'allowed_cities'
      ? 'allowed_relations'
      : 'forbidden_relations';

  const existingRule = rules.find(r => r.type === ruleType) as
    | AllowedRelationsRule
    | ForbiddenRelationsRule
    | undefined;

  const currentRelation = existingRule?.relations.find(r => r.id === relationId);
  const fromCode = currentRelation?.from || '';
  const toCode = currentRelation?.to || '';

  const [dataFrom, setDataFrom] = useState<CountriesAutocomplete>([]);
  const [dataTo, setDataTo] = useState<CountriesAutocomplete>([]);
  const [highlightFrom, setHighlightFrom] = useState('');
  const [highlightTo, setHighlightTo] = useState('');

  const city = useCityRelations({ from: fromCode, to: toCode }, isCity);

  const getValueFromCode = (code: string): string => {
    if (!code) return '';
    if (isCity) return city.resolveLabel(code);
    const option = CountriesOptions.find(option => option.subtitle === code);
    return option?.title || '';
  };

  const updateRule = (newFrom: string, newTo: string) => {
    const level = isCity ? 'CITY' : 'COUNTRY';
    const isAllowed = relationType.includes('allowed');
    const allRelations = existingRule?.relations || [];

    const isEmpty = !newFrom && !newTo;
    let updatedRelations;

    if (isEmpty) {
      updatedRelations = allRelations.filter(r => r.id !== relationId);

      if (updatedRelations.length === 0 && existingRule) {
        handleRuleChange(existingRule, true);
        return;
      }
    } else {
      const relationExists = allRelations.some(r => r.id === relationId);
      updatedRelations = relationExists
        ? allRelations.map(r =>
            r.id === relationId ? { id: relationId, from: newFrom, to: newTo } : r,
          )
        : [...allRelations, { id: relationId, from: newFrom, to: newTo }];
    }

    const rule = {
      type: isAllowed ? 'allowed_relations' : 'forbidden_relations',
      level,
      relations: updatedRelations,
    } as AllowedRelationsRule | ForbiddenRelationsRule;

    handleRuleChange(rule, false);
  };

  const filterCountries = (searchQuery: string, data: CountriesAutocomplete) => {
    return data.filter(
      item =>
        item?.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item?.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  };

  const handleInputChange = (field: 'from' | 'to') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    const setHighlight = field === 'from' ? setHighlightFrom : setHighlightTo;
    const setData = field === 'from' ? setDataFrom : setDataTo;

    if (searchValue === '') {
      updateRule(field === 'from' ? '' : fromCode, field === 'to' ? '' : toCode);
    }

    setHighlight(searchValue);

    if (isCity) {
      city.searchOptions(searchValue, field);
    } else {
      setData(filterCountries(searchValue, CountriesOptions));
    }
  };

  const handleFocus = (field: 'from' | 'to') => () => {
    // Countries are a local list, so show them immediately. For cities, show the
    // first batch from the catalog so the user has options before typing.
    if (isCity) {
      city.loadInitialOptions(field);
    } else {
      const setData = field === 'from' ? setDataFrom : setDataTo;
      setData(CountriesOptions);
    }
  };

  const handleSelect = (field: 'from' | 'to') => (selectedItem: AutocompleteCountry) => {
    if (selectedItem.subtitle) {
      const newCode = selectedItem.subtitle;
      if (isCity) {
        city.cacheLabel(newCode, selectedItem.title);
      }
      updateRule(field === 'from' ? newCode : fromCode, field === 'to' ? newCode : toCode);

      if (field === 'from') {
        setHighlightFrom('');
      } else {
        setHighlightTo('');
      }

      if (isCity) {
        city.clearOptions(field);
      } else {
        (field === 'from' ? setDataFrom : setDataTo)([]);
      }
    }
  };

  const handleDelete = (field: 'from' | 'to') => () => {
    updateRule(field === 'from' ? '' : fromCode, field === 'to' ? '' : toCode);
  };

  const handleReset = (field: 'from' | 'to') => () => {
    updateRule(field === 'from' ? '' : fromCode, field === 'to' ? '' : toCode);
  };

  const getLabels = () => {
    if (isCity) {
      return { from: 'From city', to: 'To city' };
    }
    return { from: 'From country', to: 'To country' };
  };

  const labels = getLabels();

  return (
    <>
      <LegacyAutocomplete
        options={isCity ? city.options.from : dataFrom}
        value={getValueFromCode(fromCode)}
        onDebounce={handleInputChange('from')}
        onDelete={handleDelete('from')}
        onReset={handleReset('from')}
        onSelect={handleSelect('from')}
      >
        <LegacyAutocompleteInput
          id={`autocomplete-from-${relationId}`}
          label={labels.from}
          type='search'
          onFocus={handleFocus('from')}
        />
        <LegacyAutocompleteOptions label={labels.from} highlightQuery={highlightFrom} />
      </LegacyAutocomplete>
      <LegacyAutocomplete
        options={isCity ? city.options.to : dataTo}
        value={getValueFromCode(toCode)}
        onDebounce={handleInputChange('to')}
        onDelete={handleDelete('to')}
        onReset={handleReset('to')}
        onSelect={handleSelect('to')}
      >
        <LegacyAutocompleteInput
          id={`autocomplete-to-${relationId}`}
          label={labels.to}
          type='search'
          onFocus={handleFocus('to')}
        />
        <LegacyAutocompleteOptions label={labels.to} highlightQuery={highlightTo} />
      </LegacyAutocomplete>
    </>
  );
};

export default RelationPairInputs;
